import { Shell } from "@/components/layout/Shell";
import { ContractForm } from "@/components/contracts/form/ContractForm";
import { useCurrencies } from "@/hooks/useCurrencies";
import { useCategories } from "@/hooks/useCategories";
import { useContractTypes } from "@/hooks/useContractTypes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { PlanSelectDialog } from "@/components/settings/PlanSelectDialog";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { SetupGuideCTA } from "@/components/setup-guide/SetupGuideCTA";

export default function ContractCreatePage() {
  const { t } = useTranslation();
  const { useCurrenciesQuery, useCreateCurrencyMutation } = useCurrencies();
  const { useCategoriesQuery } = useCategories();
  const { useContractTypesQuery } = useContractTypes();
  
  const { data: currencies = [], isLoading: currenciesLoading } = useCurrenciesQuery();
  const { data: categories = [], isLoading: categoriesLoading } = useCategoriesQuery();
  const { data: contractTypes = [], isLoading: contractTypesLoading } = useContractTypesQuery();
  
  const createCurrencyMutation = useCreateCurrencyMutation();
  const { useUserPreferencesQuery } = useUserPreferences();
  const { data: userPreferences } = useUserPreferencesQuery();

  // Generate a temporary ID for the new contract to use with services
  const tempContractId = uuidv4();
  const [initialContract, setInitialContract] = useState({
    id: tempContractId,
  });

  // If there are no currencies yet, create a default USD currency
  useEffect(() => {
    const createDefaultCurrency = async () => {
      if (!currenciesLoading && currencies.length === 0) {
        try {
          await createCurrencyMutation.mutateAsync({
            code: "USD",
            name: "US Dollar",
            symbol: "$"
          });
        } catch (error) {
          console.error("Error creating default currency:", error);
        }
      }
    };

    createDefaultCurrency();
  }, [currencies, currenciesLoading, createCurrencyMutation]);

  // Set up default values for the new contract
  useEffect(() => {
    if (userPreferences?.defaultCurrencyId && currencies.length > 0) {
      setInitialContract(prev => ({
        ...prev,
        currencyId: userPreferences.defaultCurrencyId
      }));
    }
  }, [userPreferences, currencies, tempContractId]);

  const showSetupCTAs = !currenciesLoading && !categoriesLoading && !contractTypesLoading;
  const needsSetup = showSetupCTAs && (
    currencies.length === 0 ||
    categories.length === 0 ||
    contractTypes.length === 0
  );

  return (
    <Shell>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('createNewContract')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('addNewContract')}
          </p>
        </div>

        {needsSetup && (
          <div className="flex flex-col gap-2">
            {currencies.length === 0 && (
              <SetupGuideCTA type="currencies" />
            )}
            {contractTypes.length === 0 && (
              <SetupGuideCTA type="contract-types" />
            )}
            {categories.length === 0 && (
              <SetupGuideCTA type="categories" />
            )}
          </div>
        )}

        <ContractForm 
          mode="create" 
          contract={initialContract} 
        />
      </div>
      
      <PlanSelectDialog />
    </Shell>
  );
}
